/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file state/store for nominated apps widget
 * @module Epic.AppOrchard.State.NominatedApps
 */

import { buildSharedState } from "@epic/react-redux-booster";
import store from "../../store";
import { INominatedApp, INotImplementingNowDecision } from "../../types";
import { searchHasMatch } from "../../utils/helpers";

/// TYPES ///

/** Org specific info */
interface IOrgDetails {
	hasSignedCurrentApiLicenseAgreement: boolean;
	hasSignedAnyApiLicenseAgreement: boolean;
}

export interface IState {
	// Map between the org ID and the nominated apps for a customer org
	readonly nominatedAppsForOrg: Map<number, INominatedApp[]> | undefined;
	// Whether nominated apps data has been loaded for a customer org
	readonly hasLoadedForOrg: Map<number, boolean> | undefined;
	readonly orgDetails: Map<number, IOrgDetails> | undefined;
	readonly participantTermsId: number | undefined;
	readonly apiLicenseAgreementId: number | undefined;
}

/**
 * Search params to identify the apps to select from the store
 */
interface INominatedAppsSelectorParams {
	orgId: number;
	searchTerm: string;
}

/// INIT ///

/**
 * Returns an empty initial state
 */
export function getInitialState(): IState {
	return {
		hasLoadedForOrg: undefined,
		nominatedAppsForOrg: undefined,
		participantTermsId: undefined,
		apiLicenseAgreementId: undefined,
		orgDetails: undefined,
	};
}

//#region REDUCERS

/**
 * Make JS Date objects from string dates loaded from the server
 * @param app app to transform
 * @returns nominated apps with JS Date objects
 */
function makeJSDates(app: INominatedApp): INominatedApp {
	return {
		...app,
		nominations: app.nominations.map((nom) => {
			return { ...nom, instant: new Date(nom.instant) };
		}),
		notNowDecision: app.notNowDecision && {
			...app.notNowDecision,
			instant: new Date(app.notNowDecision.instant),
		},
	};
}

/**
 * Update nominated apps for a customer org and nominated apps as loaded from server
 * @param state current state
 * @param params reducer params (org ID and apps to add)
 * @returns updated state
 */
export function updateNominatedAppsAfterLoad(
	state: IState,
	params: {
		orgId: number;
		nominatedApps: INominatedApp[];
		participantTermsId: number;
		apiLicenseAgreementId: number | null;
		hasSignedCurrentApiLicenseAgreement: boolean | null;
		hasSignedAnyApiLicenseAgreement: boolean | null;
	},
): IState {
	const {
		orgId,
		nominatedApps,
		participantTermsId,
		apiLicenseAgreementId,
		hasSignedCurrentApiLicenseAgreement,
		hasSignedAnyApiLicenseAgreement,
	} = params;

	// update state to indicate we've loaded data for this org
	const loadedMap = state.hasLoadedForOrg
		? new Map<number, boolean>([...Array.from(state.hasLoadedForOrg), [orgId, true]])
		: new Map<number, boolean>([[orgId, true]]);

	// update state to indicate we've loaded data for this org
	const orgDetails: IOrgDetails = {
		hasSignedAnyApiLicenseAgreement: hasSignedAnyApiLicenseAgreement || false,
		hasSignedCurrentApiLicenseAgreement: hasSignedCurrentApiLicenseAgreement || false,
	};
	const orgDetailsMap = state.orgDetails
		? new Map<number, IOrgDetails>([...Array.from(state.orgDetails), [orgId, orgDetails]])
		: new Map<number, IOrgDetails>([[orgId, orgDetails]]);

	const nomAppsEntryForOrg = nominatedApps.map((a) => makeJSDates(a));

	// update data loaded for the org
	const nomAppsMap = state.nominatedAppsForOrg
		? new Map<number, INominatedApp[]>([
				...Array.from(state.nominatedAppsForOrg),
				[orgId, nomAppsEntryForOrg],
		  ])
		: new Map<number, INominatedApp[]>([[orgId, nomAppsEntryForOrg]]);

	return {
		hasLoadedForOrg: loadedMap,
		nominatedAppsForOrg: nomAppsMap,
		participantTermsId: participantTermsId,
		apiLicenseAgreementId: apiLicenseAgreementId || undefined,
		orgDetails: orgDetailsMap,
	};
}

/**
 * Add or update not implementing now details
 * @param state current state
 * @param params reducer params (customer org ID and apps to add)
 * @returns updated state
 */
export function updateNotImplementingNowDetails(
	state: IState,
	params: { appId: number; orgId: number; notNowDecision: INotImplementingNowDecision | null },
): IState {
	const { appId, orgId, notNowDecision } = params;
	const currentApps = getAppsOfType(state, orgId, "", undefined);
	const updatedApps = currentApps.map((app) =>
		app.appId !== appId
			? app
			: {
					...app,
					notNowDecision: notNowDecision
						? {
								privateReason: notNowDecision.privateReason,
								publicReason: notNowDecision.publicReason,
								userName: notNowDecision.userName,
								instant: new Date(notNowDecision.instant),
								appVersionId: notNowDecision.appVersionId,
						  }
						: null,
			  },
	);

	return {
		...state,
		nominatedAppsForOrg: new Map<number, INominatedApp[]>([
			...Array.from(state.nominatedAppsForOrg!),
			[orgId, updatedApps],
		]),
	};
}

/**
 * Remove app from the set of nominated apps after the customer has requested a download from the vendor
 * @param state current state
 * @param params reducer params (customer org ID and client app ID)
 * @returns updated state
 */
export function removeAppAfterDownloadRequest(
	state: IState,
	params: { appId: number; orgId: number },
): IState {
	const { appId, orgId } = params;
	const currentApps = getAppsOfType(state, orgId, "", undefined);
	const updatedApps = currentApps.filter((app) => app.appId !== appId);

	return {
		...state,
		nominatedAppsForOrg: new Map<number, INominatedApp[]>([
			...Array.from(state.nominatedAppsForOrg!),
			[orgId, updatedApps],
		]),
	};
}

/**
 * Update state when org signed current API license terms
 * @param state current state
 * @param orgId customer org ID
 * @returns updated state
 */
export function updateOrgSignedTerms(state: IState, orgId: number): IState {
	const orgDetails = state.orgDetails?.get(orgId);
	if (!orgDetails) {
		return state;
	}

	const updatedOrgDetails: IOrgDetails = {
		...orgDetails,
		hasSignedCurrentApiLicenseAgreement: true,
		hasSignedAnyApiLicenseAgreement: true,
	};

	return {
		...state,
		orgDetails: new Map<number, IOrgDetails>([
			...Array.from(state.orgDetails!),
			[orgId, updatedOrgDetails],
		]),
	};
}

//#endregion

//#region SELECTORS

/**
 * Check whether data has been loaded for a customer org
 * @param state current state
 * @param orgId: customer org id
 * @returns
 */
export function getHasLoaded(state: IState, orgId: number): boolean {
	return state.hasLoadedForOrg !== undefined && state.hasLoadedForOrg.get(orgId) === true;
}

/**
 * Get org level information ( same across all apps for an org )
 * @param state current state
 * @returns
 */
export function getStaticOrgDetails(
	state: IState,
	orgId: number,
): [
	participantTermsId: number | undefined,
	apiLicenseAgreementId: number | undefined,
	hasSignedAnyApiLicenseAgreement: boolean | undefined,
	hasSignedCurrentApiLicenseAgreement: boolean | undefined,
] {
	if (!state.hasLoadedForOrg) {
		return [undefined, undefined, undefined, undefined];
	}
	const orgDetails = state.orgDetails?.get(orgId);
	return [
		state.participantTermsId,
		state.apiLicenseAgreementId,
		orgDetails?.hasSignedAnyApiLicenseAgreement,
		orgDetails?.hasSignedCurrentApiLicenseAgreement,
	];
}

function hasDataForOrg(state: IState, orgId: number): boolean {
	return state.nominatedAppsForOrg !== undefined && state.nominatedAppsForOrg.get(orgId) !== undefined;
}

/**
 * Helper to filter apps by search term
 * @param searchTerm search term to filter apps on
 * @param app nominated app being considered for inclusion in results
 * @returns true if passes filter else false
 */
function filterBySearchTerm(searchTerm: string, app: INominatedApp): boolean {
	return (
		searchHasMatch(searchTerm, app.appName) ||
		searchHasMatch(searchTerm, app.appOrgName) ||
		searchHasMatch(searchTerm, app.prodClientId) ||
		searchHasMatch(searchTerm, app.nonProdClientId)
	);
}

/**
 * Shared helper that filters to nominated apps of a given type + search term filtering
 *
 * Checks if data has been loaded for the org too
 * @param state current state
 * @param orgId customer org ID
 * @param searchTerm search term to further filter matching apps
 * @param filterFn function to filter to type of nominated app
 * @returns list of nominated apps matching filters
 */
function getAppsOfType(
	state: IState,
	orgId: number,
	searchTerm: string,
	filterFn: ((app: INominatedApp) => boolean) | undefined,
): INominatedApp[] {
	if (!getHasLoaded(state, orgId) || !hasDataForOrg(state, orgId)) {
		return [];
	}
	let apps = state.nominatedAppsForOrg!.get(orgId)!;
	if (filterFn && apps.length > 0) {
		apps = apps.filter(filterFn);
	}

	if (searchTerm && apps.length > 0) {
		apps = apps.filter((a) => filterBySearchTerm(searchTerm, a));
	}

	return apps;
}

/**
 * Sort needs review apps by number of nominations and then instant of nomination
 * @param a first app
 * @param b second app
 * @returns standard sort number value
 */
function sortNeedsReviewApps(a: INominatedApp, b: INominatedApp): number {
	// sort by number of nominations first if they're different
	if (a.nominations.length !== b.nominations.length) {
		return b.nominations.length - a.nominations.length;
	}
	const aMostRecentNomInst = Math.max.apply(
		null,
		a.nominations.map((n) => n.instant.getTime()),
	);
	const bMostRecentNomInst = Math.max.apply(
		null,
		b.nominations.map((n) => n.instant.getTime()),
	);
	return bMostRecentNomInst - aMostRecentNomInst;
}

/**
 * Get nominated apps for a customer (that ARE NOT marked as not implementing now)
 * @param state current state
 * @param params filter params
 */
export function getNeedsReviewApps(state: IState, params: INominatedAppsSelectorParams): INominatedApp[] {
	const { orgId, searchTerm } = params;
	const apps = getAppsOfType(
		state,
		orgId,
		searchTerm,
		(a) => a.nominations.length > 0 && a.notNowDecision === null,
	);
	return apps.sort(sortNeedsReviewApps);
}

/**
 * Get nominated apps that ARE marked as not implementing now
 * @param state current state
 * @param params filter params
 */
export function getNotImplementingNowApps(
	state: IState,
	params: INominatedAppsSelectorParams,
): INominatedApp[] {
	const { orgId, searchTerm } = params;
	const apps = getAppsOfType(state, orgId, searchTerm, (a) => a.notNowDecision !== null);
	return apps.sort((a, b) => b.notNowDecision!.instant.getTime() - a.notNowDecision!.instant.getTime());
}

//#endregion

/// BUILD IT ///

const builtState = buildSharedState({
	init: getInitialState,
	reducers: {
		updateNominatedAppsAfterLoad,
		updateNotImplementingNowDetails,
		removeAppAfterDownloadRequest,
		updateOrgSignedTerms,
	},
	selectors: {
		getHasLoaded,
		getStaticOrgDetails,
		getNeedsReviewApps,
		getNotImplementingNowApps,
	},
});
store.addSharedState(builtState.sharedState, "nominatedApps");

export const { actionCreators: nominatedAppsActions, useSharedState: useNominatedAppsState } = builtState;
